/*
 * Google search numbers per page, from the Rankings data
 * (GET /search-console/site/{id}): what a page gets from Google now and what
 * moving its queries into the top 3 would be worth.
 *
 * The backend returns rows per page (clicks, impressions, position, top
 * query) and per query (position, the page it mostly lands on, estimated
 * searches a month, and the clicks / dollars a month at position 3). Each
 * query's upside is credited to the page Google shows most for it.
 *
 * Pure (no React) so it is unit-tested in pageSearch.test.js.
 */
import { metricSlug } from '../pageMetrics';
import { slugOf } from '../rankings/rankings';

const keyOfUrl = (url) => metricSlug(slugOf(url));

const EMPTY = {
  clicks: 0, impressions: 0, position: null, prevPosition: null, topQuery: null,
  queries: [], upsideClicks: 0, upsideUsd: 0, best: null,
};

/* { "<page key>": { clicks, impressions, position, prevPosition, topQuery,
     queries: [rows, by impressions], upsideClicks, upsideUsd, best } }
   `best` is the query worth the most at the top 3, or null when none is. */
export const indexSearchByPage = (summary) => {
  const out = {};
  if (!summary || summary.error) return out;
  const entry = (key) => {
    if (!out[key]) out[key] = { ...EMPTY, queries: [] };
    return out[key];
  };
  (summary.pages || []).forEach((p) => {
    const key = keyOfUrl(p.page);
    if (key == null || !p.page) return;
    Object.assign(entry(key), {
      clicks: p.clicks || 0,
      impressions: p.impressions || 0,
      position: p.position ?? null,
      prevPosition: p.prev_position ?? null,
      topQuery: p.top_query || null,
    });
  });
  // queries[:n] are the top by clicks; tracked adds every query at 1-20 with
  // real impressions, which is where the upside is. Count each query once.
  const seen = new Set();
  [...(summary.queries || []), ...(summary.tracked || [])].forEach((q) => {
    if (!q || !q.query || !q.top_page || seen.has(q.query)) return;
    seen.add(q.query);
    const e = entry(keyOfUrl(q.top_page));
    e.queries.push(q);
    e.upsideClicks += q.gain_top3_clicks || 0;
    e.upsideUsd += q.gain_top3_usd || 0;
    if ((q.gain_top3_clicks || 0) > 0 && (!e.best || q.gain_top3_clicks > e.best.gain_top3_clicks)) e.best = q;
  });
  Object.values(out).forEach((e) => {
    e.queries.sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
    e.upsideClicks = Math.round(e.upsideClicks * 10) / 10;
    e.upsideUsd = Math.round(e.upsideUsd * 100) / 100;
  });
  return out;
};

/* The entry for a DAM page (by slug), or an empty one. */
export const searchForPage = (index, page) => {
  if (!page) return EMPTY;
  const key = metricSlug(page.slug || page.id || page._id);
  return (index && index[key]) || EMPTY;
};

/* "#8.4", "#12", "—" */
export const formatPosition = (pos) => {
  if (pos == null || Number.isNaN(Number(pos))) return '—';
  const n = Number(pos);
  return `#${n >= 10 ? Math.round(n) : n.toFixed(1)}`;
};

/* "Up 2.1" / "Down 3" / null: a lower position number is better. */
export const positionMove = (pos, prev) => {
  if (pos == null || prev == null) return null;
  const d = prev - pos;
  if (Math.abs(d) < 0.5) return null;
  const n = Math.abs(d) >= 10 ? Math.round(Math.abs(d)) : Math.abs(d).toFixed(1);
  return { up: d > 0, label: `${d > 0 ? 'Up' : 'Down'} ${n}` };
};
